import React, { useEffect, useState } from 'react';
import { FB_APP_ID, FB_APP_VERSION } from '../constants/config';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';
import FacebookManager from './FacebookManagerV2';
import InstagramManager from './InstagramManagerV2';
import { fbSdkURL } from '../utils/apiUrls';
import connect from '../utils/connect';
import settingsActions from '../actions/settings';

interface FacebookAndInstagramManagerProps {
    extendToken: (token: string, type: string) => Promise<void>;
}

const FacebookAndInstagramManager: React.FC<
    FacebookAndInstagramManagerProps
> = ({ extendToken }) => {
    const { t } = useTranslation('settings');
    const [modal, setModal] = useState<string | null>(null);

    useEffect(() => {
        // Initialize both FB and IG
        (window as any).fbAsyncInit = () => {
            (window as any).FB.init({
                appId: FB_APP_ID,
                cookie: true,
                xfbml: false,
                version: FB_APP_VERSION,
                status: true, // set this status to true, this will fix popup blocker issue
            });
        };

        initializeScript(document, 'script', 'facebook-jssdk');
    }, []);

    const initializeScript = (d: Document, s: string, id: string) => {
        const fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        const js = d.createElement(s) as HTMLScriptElement;
        js.id = id;
        js.src = '//connect.facebook.net/en_US/sdk.js';
        fjs.parentNode?.insertBefore(js, fjs);
    };

    const asyncLogin = (scopes: string) => {
        return new Promise<any>(resolve => {
            (window as any).FB.login((response: any) => resolve(response), {
                scope: scopes,
                return_scopes: true,
                enable_profile_selector: true,
            });
        });
    };

    const triggerLoginPopUp = async (
        scopes: string,
        onSuccess: (token: string) => void,
        onFailure: (response: any) => void,
        type = 'facebook',
    ) => {
        const response = await asyncLogin(scopes);
        const token = R.path(['authResponse', 'accessToken'], response) || null;
        if (token === null) {
            onFailure(response);
        } else {
            const grantedScopes =
                R.path(['authResponse', 'grantedScopes'], response) || '';
            const [scopesAreValid, missingScopes] = await validateScope(
                scopes,
                token,
                grantedScopes,
            );
            if (scopesAreValid) {
                await extendToken(token, type);
                // Pass the token directly to avoid race condition with Redux state
                onSuccess(token);
            } else {
                alert(
                    t(
                        'clientConfiguration.social.notifications.missingPermissions',
                        {
                            missingScopes: missingScopes.join('\n'),
                        },
                    ),
                );
            }
        }
    };

    const loginNew = async (
        scopes: string,
        onSuccess: (token: string) => void,
        onFailure: (response: any) => void,
        type = 'facebook',
    ) => {
        triggerLoginPopUp(scopes, onSuccess, onFailure, type);
    };

    const validateScope = async (
        expectedScopes: string,
        token: string,
        grantedScopes = '',
    ) => {
        const missingScopes = [];
        const grantedScopesArray = grantedScopes.split(',');
        const expectedScopesArray = expectedScopes.split(',');

        if (grantedScopes.length > 0) {
            expectedScopesArray.forEach(expectedScope => {
                if (!grantedScopesArray.includes(expectedScope)) {
                    missingScopes.push(expectedScope);
                }
            });
        } else {
            const response = await fetch(
                `${fbSdkURL}/me/permissions?access_token=${token}`,
            );
            const data = await response.json();
            const scopes = data.data.reduce(
                (acc: string[], permission: any) => {
                    if (permission.status === 'granted') {
                        acc.push(permission.permission);
                    }
                    return acc;
                },
                [],
            );
            expectedScopesArray.forEach(expectedScope => {
                if (!scopes.includes(expectedScope)) {
                    missingScopes.push(expectedScope);
                }
            });
        }

        return [missingScopes.length === 0, missingScopes] as [
            boolean,
            string[],
        ];
    };

    const onCloseModal = () => setModal(null);

    const switchModal = (modal: string | null) => setModal(modal);
    return (
        <>
            <FacebookManager
                login={loginNew}
                modal={modal}
                triggerModalChange={switchModal}
                onCloseModal={onCloseModal}
            />
            <InstagramManager
                login={loginNew}
                modal={modal}
                triggerModalChange={switchModal}
                onCloseModal={onCloseModal}
            />
        </>
    );
};

export default connect(
    ['user', 'instagram', 'facebook'],
    settingsActions,
)(FacebookAndInstagramManager);
